/**
 * 「그날에 걸린 신청」 한 벌 — 휴가 · 출장 · 연장근로를 **날짜별로** 편다.
 *
 * ## 왜 파일로 뺐나 (2026-09-06 · 판 C 후속)
 * 「근무 내역」 절이 **「내 근태 내역」에서 「내 근태」로 옮겨 오면서** 같은 셈이 두 화면에
 * 필요해졌다. 두 곳이 제 손으로 펴면 **같은 하루가 화면마다 다른 신청을 인다** —
 * 판 B 가 셈을 `lib/work-period.ts` 한 곳에 모은 것과 같은 사정이다.
 *
 * ★★ **기간은 하루씩 편다** — 「9/1~9/3 출장」은 9/2 에도 서야 한다. 시작일에만 세우면
 *    가운데 날을 눌렀을 때 「그날은 아무 신청도 없다」로 읽힌다.
 * ★ 낱말(종류 · 상태)은 **부르는 쪽이 준다** — 이 파일은 말을 만들지 않는다(화면마다 쓰는
 *   사전이 다르면 같은 신청이 다른 이름으로 선다).
 */

export interface DatedRequest {
  label: string;
  status: string;
}

export interface RequestSourceRange {
  startDate: string;
  endDate: string;
  type: string;
  status: string;
}

export interface RequestSourceDay {
  date: string;
  status: string;
}

const DAY_MS = 86_400_000;

/** `YYYY-MM-DD` 두 개 사이의 날을 하루씩 부른다. 못 읽는 값은 **아무 날도 만들지 않는다**. */
function eachDay(from: string, to: string, fn: (key: string) => void): void {
  const a = new Date(`${from}T00:00:00.000Z`).getTime();
  const b = new Date(`${to}T00:00:00.000Z`).getTime();
  if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return;
  for (let ms = a; ms <= b; ms += DAY_MS) fn(new Date(ms).toISOString().slice(0, 10));
}

/**
 * 날짜 → 그날 걸린 신청들.
 *
 * @param input.leaves    휴가 — 기간이다.
 * @param input.trips     출장 · 외근 — 기간이다.
 * @param input.overtime  연장근로 — 하루짜리다.
 * @param input.typeLabel 종류 낱말(부르는 쪽의 사전).
 * @param input.statusLabel 상태 낱말(같은 사전).
 * @param input.overtimeLabel 연장근로의 이름 — 종류 칸이 없어 따로 받는다.
 */
export function buildRequestsByDate(input: {
  leaves: readonly RequestSourceRange[];
  trips: readonly RequestSourceRange[];
  overtime: readonly RequestSourceDay[];
  typeLabel: (type: string) => string;
  statusLabel: (status: string) => string;
  overtimeLabel: string;
}): Map<string, DatedRequest[]> {
  const out = new Map<string, DatedRequest[]>();
  const push = (key: string, label: string, status: string) => {
    const list = out.get(key);
    if (list) list.push({ label, status });
    else out.set(key, [{ label, status }]);
  };
  for (const l of input.leaves) {
    eachDay(l.startDate, l.endDate, (k) => push(k, input.typeLabel(l.type), input.statusLabel(l.status)));
  }
  for (const t of input.trips) {
    eachDay(t.startDate, t.endDate, (k) => push(k, input.typeLabel(t.type), input.statusLabel(t.status)));
  }
  for (const o of input.overtime) push(o.date, input.overtimeLabel, input.statusLabel(o.status));
  return out;
}
